import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import Logo from '@/components/Logo';
import { Link } from 'react-router-dom';
import { BUSINESS } from '@/lib/site';
import { ArrowRight, CheckIcon, CloseIcon, MailIcon, PhoneIcon, ClockIcon, ShieldIcon } from '@/components/icons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = 'nft_admin_token';

const STATUS_LABELS = {
  new: 'New',
  contacted: 'Contacted',
  booked: 'Booked',
  closed: 'Closed',
};
const STATUS_COLORS = {
  new: 'bg-[#FC4E2C] text-white',
  contacted: 'bg-[#1A24B5] text-white',
  booked: 'bg-[#2D8653] text-white',
  closed: 'bg-[#060D9C] text-white',
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || '');
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [pwInput, setPwInput] = useState('');
  const [authError, setAuthError] = useState('');

  const headers = { 'X-Admin-Password': token };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, s] = await Promise.all([
        axios.get(`${API}/bookings`, { headers }),
        axios.get(`${API}/stats`, { headers }),
      ]);
      setBookings(b.data);
      setStats(s.data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(STORAGE_KEY);
        setToken('');
      } else {
        toast.error('Failed to load bookings');
      }
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (token) load(); }, [token, load]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    try {
      const res = await axios.post(`${API}/admin/login`, { password: pwInput });
      const t = res.data.token;
      localStorage.setItem(STORAGE_KEY, t);
      setToken(t);
      setPwInput('');
    } catch (e) {
      setAuthError('Invalid password');
    }
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken('');
    setBookings([]);
    setStats(null);
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/bookings/${id}`, { status }, { headers });
      toast.success(`Status updated to ${STATUS_LABELS[status]}`);
      await load();
      if (selected) setSelected({ ...selected, status });
    } catch (e) {
      toast.error('Update failed');
    }
  };

  const updateNotes = async (id, notes) => {
    try {
      await axios.patch(`${API}/bookings/${id}`, { admin_notes: notes }, { headers });
      toast.success('Notes saved');
      await load();
    } catch (e) {
      toast.error('Save failed');
    }
  };

  const removeBooking = async (id) => {
    if (!window.confirm('Delete this enquiry permanently?')) return;
    try {
      await axios.delete(`${API}/bookings/${id}`, { headers });
      toast.success('Deleted');
      setSelected(null);
      load();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const filtered = bookings.filter(b => filter === 'all' || b.status === filter);

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#060D9C] text-white p-4">
        <Toaster position="top-right" richColors />
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-6"><Logo variant="dark" /></div>
          <form onSubmit={handleLogin} className="bg-white text-[#060D9C] rounded-2xl p-6 lg:p-8" data-testid="admin-login-form">
            <div className="label-eyebrow text-[#FC4E2C] mb-1">Admin Access</div>
            <h1 className="font-display font-extrabold uppercase text-3xl mb-4">Sign in</h1>
            <label className="block mb-3">
              <span className="label-eyebrow text-[#060D9C]/70 mb-1.5 block">Password</span>
              <input
                type="password"
                value={pwInput}
                onChange={(e) => setPwInput(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border-2 border-[#060D9C]/15 focus:border-[#FC4E2C] outline-none"
                placeholder="••••••••"
                data-testid="admin-password-input"
                autoFocus
              />
            </label>
            {authError && <div className="text-sm text-[#c0392b] mb-3">{authError}</div>}
            <button type="submit" className="btn-primary-orange w-full justify-center" data-testid="admin-login-button">
              Sign In <ArrowRight width={16} height={16} />
            </button>
            <div className="mt-4 text-xs text-[#060D9C]/60">
              <strong>Default password:</strong> <code className="font-mono-cert">nft-admin-2024</code> (set ADMIN_PASSWORD env to change)
            </div>
          </form>
          <div className="mt-4 text-center">
            <Link to="/" className="text-white/60 hover:text-[#FC4E2C] text-sm">← Back to site</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F5F7]">
      <Toaster position="top-right" richColors />
      {/* Top bar */}
      <div className="bg-[#060D9C] text-white">
        <div className="hi-vis-stripe h-1" />
        <div className="nft-container py-3 flex items-center justify-between">
          <Logo variant="dark" compact />
          <div className="flex items-center gap-3">
            <Link to="/" className="text-xs text-white/70 hover:text-[#FC4E2C]">View site →</Link>
            <button onClick={logout} className="text-xs btn-outline-white py-1.5 px-3" data-testid="admin-logout">
              Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="nft-container py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="label-eyebrow text-[#FC4E2C]">NFT Admin</div>
            <h1 className="font-display font-extrabold uppercase text-3xl lg:text-4xl text-[#060D9C]">Booking Enquiries</h1>
          </div>
          <button onClick={load} className="btn-secondary-navy py-2 px-4 text-xs" data-testid="admin-refresh">
            Refresh
          </button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <StatCard label="Total" value={stats.total} color="#060D9C" />
            <StatCard label="New" value={stats.new} color="#FC4E2C" />
            <StatCard label="Contacted" value={stats.contacted} color="#1A24B5" />
            <StatCard label="Booked" value={stats.booked} color="#2D8653" />
            <StatCard label="Closed" value={stats.closed} color="#6B7280" />
          </div>
        )}

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="admin-filter-tabs">
          {['all', 'new', 'contacted', 'booked', 'closed'].map(s => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${filter === s ? 'bg-[#060D9C] text-white' : 'bg-white border border-[#060D9C]/15 text-[#060D9C] hover:border-[#060D9C]/30'}`}
              style={{ fontFamily: 'Barlow Semi Condensed' }}
              data-testid={`admin-filter-${s}`}
            >
              {s === 'all' ? 'All' : STATUS_LABELS[s]}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="surface-card overflow-hidden" data-testid="admin-enquiries-table">
          {loading ? (
            <div className="p-10 text-center text-[#060D9C]/60">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-[#060D9C]/60">
              <ShieldIcon width={36} height={36} className="mx-auto text-[#060D9C]/30 mb-2" />
              No enquiries match this filter.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#060D9C] text-white">
                    <th className="text-left px-4 py-3 label-eyebrow text-[#FC4E2C]">Reference</th>
                    <th className="text-left px-4 py-3 label-eyebrow text-[#FC4E2C]">Name</th>
                    <th className="text-left px-4 py-3 label-eyebrow text-[#FC4E2C] hidden md:table-cell">Course</th>
                    <th className="text-left px-4 py-3 label-eyebrow text-[#FC4E2C] hidden lg:table-cell">Trainees</th>
                    <th className="text-left px-4 py-3 label-eyebrow text-[#FC4E2C]">Status</th>
                    <th className="text-left px-4 py-3 label-eyebrow text-[#FC4E2C] hidden lg:table-cell">Received</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((b) => (
                    <tr
                      key={b.id}
                      onClick={() => setSelected(b)}
                      className="border-t border-[#060D9C]/8 hover:bg-[#FC4E2C]/5 cursor-pointer transition-colors"
                      data-testid="admin-enquiry-row"
                    >
                      <td className="px-4 py-3 font-mono-cert text-xs font-bold">{b.reference}</td>
                      <td className="px-4 py-3">
                        <div className="font-bold">{b.name}</div>
                        {b.company && <div className="text-xs text-[#060D9C]/60">{b.company}</div>}
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">{b.course}</td>
                      <td className="px-4 py-3 hidden lg:table-cell">{b.trainees ?? '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-1 rounded text-xs font-bold uppercase ${STATUS_COLORS[b.status]}`} style={{ fontFamily: 'Barlow Semi Condensed' }}>
                          {STATUS_LABELS[b.status]}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-[#060D9C]/60 hidden lg:table-cell">
                        {new Date(b.created_at).toLocaleString('en-NZ', { dateStyle: 'short', timeStyle: 'short' })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="admin-detail-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSelected(null)} />
          <div className="relative w-full max-w-xl bg-white h-full overflow-y-auto">
            <div className="bg-[#060D9C] text-white p-5 sticky top-0 z-10">
              <div className="flex items-center justify-between">
                <div>
                  <div className="label-eyebrow text-[#FC4E2C]">Enquiry</div>
                  <div className="font-mono-cert text-lg font-bold">{selected.reference}</div>
                </div>
                <button onClick={() => setSelected(null)} className="p-2 hover:bg-white/10 rounded" aria-label="Close" data-testid="admin-close-detail">
                  <CloseIcon />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <h2 className="font-display font-extrabold uppercase text-3xl text-[#060D9C] leading-tight">{selected.name}</h2>
                {selected.company && <div className="text-[#060D9C]/70">{selected.company}</div>}
              </div>

              <div>
                <div className="label-eyebrow text-[#060D9C]/60 mb-2">Status</div>
                <select
                  value={selected.status}
                  onChange={(e) => updateStatus(selected.id, e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border-2 border-[#060D9C]/15 focus:border-[#FC4E2C] outline-none text-sm font-bold"
                  data-testid="admin-enquiry-status-select"
                >
                  {Object.entries(STATUS_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Info label="Email" value={<a href={`mailto:${selected.email}`} className="text-[#060D9C] hover:text-[#FC4E2C] break-all">{selected.email}</a>} icon={MailIcon} />
                <Info label="Phone" value={selected.phone ? <a href={`tel:${selected.phone}`} className="text-[#060D9C] hover:text-[#FC4E2C]">{selected.phone}</a> : '—'} icon={PhoneIcon} />
                <Info label="Course" value={selected.course} />
                <Info label="Trainees" value={selected.trainees ?? '—'} />
                <Info label="Preferred Date" value={selected.preferred_date || '—'} icon={ClockIcon} />
                <Info label="On-site" value={selected.on_site} />
                <Info label="NZQA" value={selected.nzqa ? 'Yes' : 'No'} />
                <Info label="F Endorsement" value={selected.f_endorsement ? 'Yes' : 'No'} />
              </div>

              {selected.message && (
                <div>
                  <div className="label-eyebrow text-[#060D9C]/60 mb-1">Message</div>
                  <div className="callout-steel text-sm whitespace-pre-wrap">{selected.message}</div>
                </div>
              )}

              <div>
                <div className="label-eyebrow text-[#060D9C]/60 mb-1">Admin Notes</div>
                <textarea
                  defaultValue={selected.admin_notes || ''}
                  rows={4}
                  onBlur={(e) => updateNotes(selected.id, e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border-2 border-[#060D9C]/15 focus:border-[#FC4E2C] outline-none text-sm"
                  placeholder="Internal notes (saves on blur)…"
                  data-testid="admin-notes-textarea"
                />
              </div>

              <div className="text-xs text-[#060D9C]/55">
                <div>Received: {new Date(selected.created_at).toLocaleString('en-NZ')}</div>
                <div>Updated: {new Date(selected.updated_at).toLocaleString('en-NZ')}</div>
              </div>

              <div className="pt-4 border-t border-[#060D9C]/10 flex flex-wrap gap-2">
                <a href={`mailto:${selected.email}?subject=Re: ${selected.reference}`} className="btn-primary-orange" data-testid="admin-reply-email">
                  <MailIcon width={16} height={16} /> Reply by email
                </a>
                <button onClick={() => removeBooking(selected.id)} className="btn-secondary-navy bg-[#c0392b] border-[#c0392b] hover:bg-[#8a1c0a]" data-testid="admin-delete-enquiry">
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="surface-card p-4">
      <div className="label-eyebrow text-[#060D9C]/60">{label}</div>
      <div className="font-display font-extrabold text-3xl mt-1" style={{ color }}>{value}</div>
    </div>
  );
}

function Info({ label, value, icon: Icon }) {
  return (
    <div>
      <div className="label-eyebrow text-[#060D9C]/60 mb-0.5 flex items-center gap-1">
        {Icon && <Icon width={12} height={12} />} {label}
      </div>
      <div className="text-sm font-bold">{value}</div>
    </div>
  );
}
